import type { ColumnDef } from '@tanstack/react-table';

import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { formatDate } from '@/lib/dateFormatter';
import { PremiumStores } from '@/services/premiumStores/premiumStores.types';

export function getColumns(t: (key: string, values?: Record<string, unknown>) => string): ColumnDef<PremiumStores>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem] text-caption text-primary">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'storeNameEng',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeInfo')} />,
      cell: ({ row }) => {
        const storeName = row.original.storeName;
        const storeNameEng = row.original.storeNameEng;
        const storeImage = row.original.clubLogo;

        return (
          <div className="flex items-center space-x-3 max-w-[20rem] truncate">
            <img
              src={storeImage?.path}
              alt={storeImage?.alt ?? ''}
              className="w-8 h-8 shrink-0 rounded-full object-cover"
            />
            <div className="truncate">
              <div className="font-medium text-caption text-primary">{storeNameEng}</div>
              <div className="text-foreground-subtle font-medium text-caption">{storeName}</div>
            </div>
          </div>
        );
      },
      meta: {
        label: 'Store Info',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'telephone',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.telephone')} />,
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('telephone')}</div>,
      meta: {
        label: 'Telephone',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'prefecture',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.location')} />,
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('prefecture')}</div>,
      meta: {
        label: 'Prefecture',
      },
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => {
        const date = new Date(Number(row.getValue<Date>('createdDate')));
        return (
          <div className="font-medium whitespace-nowrap text-caption text-primary">
            {formatDate(date, 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      meta: {
        label: 'Created Date',
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedDate')} />
      ),
      cell: ({ row }) => {
        const date = new Date(Number(row.getValue('updatedDate')));
        return (
          <div className="font-medium whitespace-nowrap text-caption text-primary">
            {formatDate(date, 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      meta: {
        label: 'Updated Date',
      },
      enableHiding: false,
    },
  ];
}
